import { useEffect, useRef, useState } from 'react'
import { Button, Carousel } from 'antd'
import { useTranslation } from 'next-i18next'

const carouselSettings = {
  slidesToShow: 3,
  slidesToScroll: 1,
  infinite: false,
  dots: false,
  arrows: false,
  swipeToSlide: true,
  responsive: [
    {
      breakpoint: 991.98,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 767,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
}

const CustomReviewsCarousel = ({
  reviews = [],
  profileUrl = 'https://it.trustpilot.com/review/smartcaf.it',
}) => {
  const { t } = useTranslation('Home')
  const carouselRef = useRef(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [windowWidth, setWindowWidth] = useState(1200)

  useEffect(() => {
    setCurrentSlide(0)
  }, [reviews])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handleResize = () => setWindowWidth(window.innerWidth)
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const isMobile = windowWidth < 767
  const isTablet = windowWidth >= 767 && windowWidth < 992

  let slidesToShow = 3
  if (isMobile) slidesToShow = 1
  else if (isTablet) slidesToShow = 2

  const totalSlides = Math.max(1, reviews.length - slidesToShow + 1)
  const currentActualPage = Math.min(currentSlide + 1, totalSlides)

  const handlePrev = () => {
    if (carouselRef.current) {
      carouselRef.current.prev()
    }
  }

  const handleNext = () => {
    if (carouselRef.current) {
      carouselRef.current.next()
    }
  }

  if (!reviews.length) {
    return null
  }

  return (
    <>
      <div className='max-w-[1440px] mx-auto mb-40 md:mb-60 [&_.slick-track]:flex [&_.slick-track]:items-stretch [&_.slick-slide]:h-auto [&_.slick-slide>div]:h-full'>
        <Carousel
          ref={carouselRef}
          afterChange={(current) => setCurrentSlide(current)}
          {...carouselSettings}
        >
          {reviews.map((review) => (
            <div key={`${review.author}-${review.title}`} className='h-full px-8 pb-16 pt-8'>
              <a
                href={review.url || profileUrl}
                target='_blank'
                rel='noreferrer'
                aria-label={`Apri la recensione Trustpilot di ${review.author}`}
                className='mx-auto flex h-full max-w-[400px] flex-col justify-between gap-24 overflow-hidden rounded-[8px] bg-white p-24 shadow-lg transition-transform duration-200 hover:-translate-y-[2px] hover:shadow-[0px_8px_24px_0px_rgba(0,0,0,0.12)] no-underline md:min-h-[350px]'
                dir='ltr'
              >
                <article className='flex h-full flex-col justify-between gap-24'>
                  <div className='flex min-h-0 flex-1 flex-col gap-16'>
                    <div className='flex flex-col gap-8'>
                      <div className='flex items-center gap-8'>
                        <TrustpilotMark />
                        <p className='mb-0 text-[18px] font-bold capitalize leading-[20px] tracking-[0.18px] text-black md:text-[20px] md:tracking-[0.2px]'>
                          Trustpilot
                        </p>
                      </div>
                      <RatingStars rating={review.rating} />
                    </div>

                    <div className='min-h-0 overflow-hidden'>
                      <h3 className='mb-8 overflow-hidden line-clamp-2 text-[18px] font-bold leading-[24px] text-black'>
                        {review.title}
                      </h3>
                      <p className='mb-0 overflow-hidden line-clamp-6 text-[16px] leading-[22px] text-dimmed'>
                        {review.text}
                      </p>
                    </div>
                  </div>

                  <div className='flex items-center gap-8 mt-auto'>
                    <ProfileBadge />
                    <p className='mb-0 text-[18px] font-semibold leading-[24px] text-black'>
                      {review.author}
                    </p>
                    {review.verified && <VerifiedBadge />}
                  </div>
                </article>
              </a>
            </div>
          ))}
        </Carousel>
      </div>

      {totalSlides > 1 && (
        <div className='mt-32 flex items-center justify-center gap-16'>
          <Button
            aria-label={t('reviews.previous')}
            className='btn-green-outlined flex items-center justify-center h-auto min-w-[48px] rounded-full px-16 py-10 disabled:opacity-50'
            disabled={currentSlide === 0}
            onClick={handlePrev}
          >
            <ArrowLeftIcon />
          </Button>

          <p className='mb-0 min-w-[72px] text-center text-[14px] font-medium text-dimmed'>
            {currentActualPage} / {totalSlides}
          </p>

          <Button
            aria-label={t('reviews.next')}
            className='btn-green-outlined flex items-center justify-center h-auto min-w-[48px] rounded-full px-16 py-10 disabled:opacity-50'
            disabled={currentSlide >= totalSlides - 1}
            onClick={handleNext}
          >
            <ArrowRightIcon />
          </Button>
        </div>
      )}
    </>
  )
}

const TrustpilotMark = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
    className='shrink-0'
  >
    <path
      d='M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z'
      fill='#00B67A'
    />
  </svg>
)

const ArrowLeftIcon = () => (
  <svg
    width='18'
    height='18'
    viewBox='0 0 18 18'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <path
      d='M10.5 4.5L6 9L10.5 13.5'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const ArrowRightIcon = () => (
  <svg
    width='18'
    height='18'
    viewBox='0 0 18 18'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <path
      d='M7.5 4.5L12 9L7.5 13.5'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const RatingStars = ({ rating = 5 }) => (
  <div className='flex gap-4 leading-none'>
    {Array.from({ length: 5 }).map((_, index) => (
      <span
        key={index}
        className={`flex h-24 w-24 items-center justify-center ${
          index < rating ? 'bg-[#00B67A]' : 'bg-[#DCDCE6]'
        }`}
      >
        <svg
          width='18'
          height='18'
          viewBox='0 0 24 24'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          aria-hidden='true'
        >
          <path
            d='M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z'
            fill='white'
          />
        </svg>
      </span>
    ))}
  </div>
)

const ProfileBadge = () => (
  <span className='flex h-32 w-32 items-center justify-center rounded-full bg-green'>
    <svg
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden='true'
    >
      <path
        d='M12 12C13.6569 12 15 10.6569 15 9C15 7.34315 13.6569 6 12 6C10.3431 6 9 7.34315 9 9C9 10.6569 10.3431 12 12 12Z'
        stroke='white'
        strokeWidth='1.8'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M6.75 18C7.51336 15.9724 9.46856 14.5 12 14.5C14.5314 14.5 16.4866 15.9724 17.25 18'
        stroke='white'
        strokeWidth='1.8'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  </span>
)

const VerifiedBadge = () => (
  <svg
    width='18'
    height='18'
    viewBox='0 0 18 18'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
    className='shrink-0'
  >
    <circle cx='9' cy='9' r='6.75' fill='#1877F2' />
    <path
      d='M6.6 9L8.25 10.65L11.4 7.5'
      stroke='white'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

export default CustomReviewsCarousel